// sessionStorageManager.js - Enhanced with session cleanup methods

/**
 * Session Storage Manager
 * This utility provides methods for managing objects in local storage
 * in a consistent way across the application with project-based hierarchy support.
 */
class SessionStorageManager {
  constructor() {
    // Default project ID constant - matches backend
    this.DEFAULT_PROJECT_ID = "11111111-1111-1111-1111-111111111111";
  }

  /**
   * Get the project ID to use, defaulting to the default project if none provided
   * @param {string} projectId - Optional project ID
   * @returns {string} - Project ID to use
   */
  _getProjectId(projectId) {
    return projectId || this.DEFAULT_PROJECT_ID;
  }

  /**
   * Create a storage key with project hierarchy
   * @param {string} key - The base key
   * @param {string} namespace - Optional namespace (usually sessionId)
   * @param {string} projectId - Optional project ID
   * @returns {string} - The formatted storage key
   */
  _createStorageKey(key, namespace = '', projectId = null) {
    const proj_id = this._getProjectId(projectId);
    
    if (namespace) {
      return `${namespace}:project:${proj_id}:${key}`;
    } else {
      return `project:${proj_id}:${key}`;
    }
  }

  /**
   * Check if a localStorage key was created by sessionStorageManager
   * @param {string} key - The localStorage key
   * @returns {boolean} - True if key follows sessionStorageManager pattern
   */
  _isSessionStorageManagerKey(key) {
    // Keys created by sessionStorageManager have specific patterns
    return key.includes(':project:') || 
           key.startsWith('project:') ||
           key.includes('agent_state:') ||
           key.includes('agent_key:') ||
           key.includes('component_map:') ||
           key.includes('project_info:') ||
           key === 'current_project_id';
  }

  /**
   * Check if a string is valid JSON
   * @param {string} str - String to check
   * @returns {boolean} - True if valid JSON
   */
  _isValidJSON(str) {
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === 'object' && parsed !== null;
    } catch (e) {
      return false;
    }
  }

  /**
   * Set an item in local storage with namespace, project support, and expiration
   * @param {string} key - The storage key
   * @param {any} value - The value to store
   * @param {string} namespace - Optional namespace to prefix key (usually sessionId)
   * @param {number} expiryInHours - Optional expiration time in hours
   * @param {string} projectId - Optional project ID
   */
  setItem(key, value, namespace = '', expiryInHours = 24, projectId = null) {
    try {
      // Create project-aware storage key
      const storageKey = this._createStorageKey(key, namespace, projectId);
      
      // Add expiration timestamp and project info
      const storageObj = {
        value,
        timestamp: Date.now(),
        expiry: expiryInHours > 0 ? Date.now() + (expiryInHours * 60 * 60 * 1000) : null,
        projectId: this._getProjectId(projectId)
      };
      
      // Store serialized object
      localStorage.setItem(storageKey, JSON.stringify(storageObj));
      return true;
    } catch (e) {
      console.error(`Error storing ${key} in local storage:`, e);
      return false;
    }
  }

  /**
   * Get an item from local storage with expiration check and project support
   * @param {string} key - The storage key
   * @param {string} namespace - Optional namespace prefix (usually sessionId)
   * @param {any} defaultValue - Optional default value if not found
   * @param {string} projectId - Optional project ID
   * @returns {any} - The retrieved value or defaultValue
   */
  getItem(key, namespace = '', defaultValue = null, projectId = null) {
    try {
      // Create project-aware storage key
      const storageKey = this._createStorageKey(key, namespace, projectId);
      
      // Get serialized object
      const serializedObj = localStorage.getItem(storageKey);
      if (!serializedObj) {
        return defaultValue;
      }
      
      // Parse storage object
      const storageObj = JSON.parse(serializedObj);
      
      // Check expiration if set
      if (storageObj.expiry && storageObj.expiry < Date.now()) {
        // Item expired, remove it
        localStorage.removeItem(storageKey);
        return defaultValue;
      }
      
      return storageObj.value;
    } catch (e) {
      console.error(`Error retrieving ${key} from local storage:`, e);
      return defaultValue;
    }
  }

  /**
   * Remove an item from local storage with project support
   * @param {string} key - The storage key
   * @param {string} namespace - Optional namespace prefix
   * @param {string} projectId - Optional project ID
   * @returns {boolean} - True if successful
   */
  removeItem(key, namespace = '', projectId = null) {
    try {
      // Create project-aware storage key
      const storageKey = this._createStorageKey(key, namespace, projectId);
      
      // Remove the item
      localStorage.removeItem(storageKey);
      return true;
    } catch (e) {
      console.error(`Error removing ${key} from local storage:`, e);
      return false;
    }
  }

  /**
   * Clear ALL session data for a specific session ID
   * @param {string} sessionId - The session ID to clear data for
   * @returns {number} - Number of items removed
   */
  clearSessionData(sessionId) {
    let removed = 0;
    try {
      if (!sessionId) {
        console.warn('No session ID provided for clearSessionData');
        return 0;
      }

      const keys = Object.keys(localStorage);
      const sessionPrefix = `${sessionId}:`;
      
      // Remove all keys that start with the session ID
      keys.forEach(key => {
        if (key.startsWith(sessionPrefix)) {
          try {
            localStorage.removeItem(key);
            removed++;
            console.log(`Removed session key: ${key}`);
          } catch (e) {
            console.warn(`Error removing session key ${key}:`, e);
          }
        }
      });
      
      console.log(`Cleared ${removed} items for session ${sessionId}`);
      return removed;
    } catch (e) {
      console.error(`Error clearing session data for ${sessionId}:`, e);
      return removed;
    }
  }

  /**
   * Clear ALL sessionStorageManager data (useful for fresh login)
   * @returns {number} - Number of items removed
   */
  clearAllSessionStorageManagerData() {
    let removed = 0;
    try {
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (this._isSessionStorageManagerKey(key)) {
          try {
            localStorage.removeItem(key);
            removed++;
            console.log(`Removed sessionStorageManager key: ${key}`);
          } catch (e) {
            console.warn(`Error removing key ${key}:`, e);
          }
        }
      });
      
      console.log(`Cleared ${removed} sessionStorageManager items`);
      return removed;
    } catch (e) {
      console.error('Error clearing all sessionStorageManager data:', e);
      return removed;
    }
  }

  /**
   * Clear all localStorage data (nuclear option)
   * @returns {boolean} - True if successful
   */
  clearAllLocalStorageData() {
    try {
      const keyCount = localStorage.length;
      localStorage.clear();
      console.log(`Cleared all ${keyCount} localStorage items`);
      return true;
    } catch (e) {
      console.error('Error clearing all localStorage data:', e);
      return false;
    }
  }

  /**
   * Store agent state in local storage with project support
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @param {object} state - The agent state to store
   * @param {string} projectId - Optional project ID
   * @returns {boolean} - True if successful
   */
  setAgentState(sessionId, agentId, state, projectId = null) {
    return this.setItem(`agent_state:${agentId}`, state, sessionId, 24, projectId);
  }

  /**
   * Get agent state from local storage with project support
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @param {object} defaultState - Optional default state if not found
   * @param {string} projectId - Optional project ID
   * @returns {object} - The agent state or defaultState
   */
  getAgentState(sessionId, agentId, defaultState = {}, projectId = null) {
    return this.getItem(`agent_state:${agentId}`, sessionId, defaultState, projectId);
  }

  /**
   * Store agent key in local storage with project support
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @param {string} agentKey - The agent key
   * @param {string} projectId - Optional project ID
   * @returns {boolean} - True if successful
   */
  setAgentKey(sessionId, agentId, agentKey, projectId = null) {
    return this.setItem(`agent_key:${agentId}`, agentKey, sessionId, 24, projectId);
  }

  /**
   * Get agent key from local storage with project support
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @param {string} projectId - Optional project ID
   * @returns {string|null} - The agent key or null
   */
  getAgentKey(sessionId, agentId, projectId = null) {
    return this.getItem(`agent_key:${agentId}`, sessionId, null, projectId);
  }

  /**
   * Store component map in local storage with project support
   * @param {string} sessionId - The user's session ID
   * @param {string} agentKey - The agent key
   * @param {object} componentMap - The component map
   * @param {string} projectId - Optional project ID
   * @returns {boolean} - True if successful
   */
  setComponentMap(sessionId, agentKey, componentMap, projectId = null) {
    return this.setItem(`component_map:${agentKey}`, componentMap, sessionId, 24, projectId);
  }

  /**
   * Get component map from local storage with project support
   * @param {string} sessionId - The user's session ID
   * @param {string} agentKey - The agent key
   * @param {string} projectId - Optional project ID
   * @returns {object|null} - The component map or null
   */
  getComponentMap(sessionId, agentKey, projectId = null) {
    return this.getItem(`component_map:${agentKey}`, sessionId, null, projectId);
  }

  /**
   * Store project information in local storage
   * @param {string} sessionId - The user's session ID
   * @param {string} projectId - The project ID
   * @param {object} projectInfo - The project information
   * @returns {boolean} - True if successful
   */
  setProjectInfo(sessionId, projectId, projectInfo) {
    return this.setItem(`project_info:${projectId}`, projectInfo, sessionId, 168); // 1 week expiry
  }

  /**
   * Get project information from local storage
   * @param {string} sessionId - The user's session ID
   * @param {string} projectId - The project ID
   * @returns {object|null} - The project information or null
   */
  getProjectInfo(sessionId, projectId) {
    return this.getItem(`project_info:${projectId}`, sessionId, null);
  }

  /**
   * Get current active project ID for a session
   * @param {string} sessionId - The user's session ID
   * @returns {string} - The active project ID or default project ID
   */
  getCurrentProjectId(sessionId) {
    return this.getItem('current_project_id', sessionId, this.DEFAULT_PROJECT_ID);
  }

  /**
   * Set current active project ID for a session
   * @param {string} sessionId - The user's session ID
   * @param {string} projectId - The project ID to set as active
   * @returns {boolean} - True if successful
   */
  setCurrentProjectId(sessionId, projectId) {
    return this.setItem('current_project_id', projectId, sessionId, 168); // 1 week expiry
  }

  /**
   * Get all projects for a session
   * @param {string} sessionId - The user's session ID
   * @returns {object[]} - Array of project information objects
   */
  getAllProjects(sessionId) {
    try {
      const keys = this.getKeysByPrefix('project_info:', sessionId);
      const projects = [];
      
      keys.forEach(key => {
        try {
          const serializedObj = localStorage.getItem(key);
          if (serializedObj && this._isValidJSON(serializedObj)) {
            const storageObj = JSON.parse(serializedObj);
            
            // Check if not expired
            if (!storageObj.expiry || storageObj.expiry > Date.now()) {
              // Extract project ID from key
              const projectId = key.split('project_info:')[1].split(':')[0];
              projects.push({
                projectId,
                ...storageObj.value
              });
            }
          }
        } catch (e) {
          console.warn(`Error parsing project info for key ${key}:`, e);
        }
      });
      
      return projects;
    } catch (e) {
      console.error('Error getting all projects:', e);
      return [];
    }
  }

  /**
   * Remove all data for a specific project
   * @param {string} sessionId - The user's session ID
   * @param {string} projectId - The project ID to remove
   * @returns {number} - Number of items removed
   */
  removeProject(sessionId, projectId) {
    try {
      const searchPrefix = `${sessionId}:project:${projectId}:`;
      const keys = Object.keys(localStorage).filter(key => key.startsWith(searchPrefix));
      
      let removed = 0;
      keys.forEach(key => {
        localStorage.removeItem(key);
        removed++;
      });
      
      // Also remove project info
      this.removeItem(`project_info:${projectId}`, sessionId);
      removed++;
      
      console.log(`Removed ${removed} items for project ${projectId}`);
      return removed;
    } catch (e) {
      console.error(`Error removing project ${projectId}:`, e);
      return 0;
    }
  }

  /**
   * Cleanup expired items in local storage
   * @returns {number} - Number of items removed
   */
  cleanupExpiredItems() {
    let removed = 0;
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        try {
          // Only process keys that were created by sessionStorageManager
          if (!this._isSessionStorageManagerKey(key)) {
            return; // Skip non-sessionStorageManager keys
          }
          
          const serializedObj = localStorage.getItem(key);
          if (serializedObj && this._isValidJSON(serializedObj)) {
            const storageObj = JSON.parse(serializedObj);
            
            // Check if it has the expected structure and is expired
            if (storageObj && 
                typeof storageObj === 'object' && 
                storageObj.expiry && 
                storageObj.expiry < now) {
              localStorage.removeItem(key);
              removed++;
            }
          }
        } catch (e) {
          // Skip this item if there's an error
          console.warn(`Error parsing item ${key}:`, e);
        }
      });
      
      if (removed > 0) {
        console.log(`Cleaned up ${removed} expired items`);
      }
      return removed;
    } catch (e) {
      console.error('Error cleaning up expired items:', e);
      return removed;
    }
  }

  /**
   * Get all keys with a specific prefix, optionally within a project
   * @param {string} prefix - The key prefix to search for
   * @param {string} namespace - Optional namespace (usually sessionId)
   * @param {string} projectId - Optional project ID to filter by
   * @returns {string[]} - Array of matching keys
   */
  getKeysByPrefix(prefix, namespace = '', projectId = null) {
    try {
      const keys = Object.keys(localStorage);
      let searchPrefix;
      
      if (projectId) {
        // Search within specific project
        const proj_id = this._getProjectId(projectId);
        searchPrefix = namespace ? `${namespace}:project:${proj_id}:${prefix}` : `project:${proj_id}:${prefix}`;
      } else if (namespace) {
        // Search within namespace (could span multiple projects)
        searchPrefix = `${namespace}:${prefix}`;
      } else {
        // Search globally
        searchPrefix = prefix;
      }
      
      return keys.filter(key => key.startsWith(searchPrefix));
    } catch (e) {
      console.error('Error getting keys by prefix:', e);
      return [];
    }
  }

  /**
   * Get storage statistics
   * @param {string} sessionId - Optional session ID to filter by
   * @param {string} projectId - Optional project ID to filter by
   * @returns {object} - Storage statistics
   */
  getStorageStats(sessionId = null, projectId = null) {
    try {
      let totalItems = 0;
      let totalSize = 0;
      let expiredItems = 0;
      let projectItems = {};
      
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        try {
          // Filter by session if provided
          if (sessionId && !key.startsWith(sessionId)) {
            return;
          }
          
          // Filter by project if provided
          if (projectId) {
            const proj_id = this._getProjectId(projectId);
            const projectPattern = `:project:${proj_id}:`;
            if (!key.includes(projectPattern)) {
              return;
            }
          }
          
          // Only process sessionStorageManager keys for stats
          if (!this._isSessionStorageManagerKey(key)) {
            return;
          }
          
          const serializedObj = localStorage.getItem(key);
          if (serializedObj && this._isValidJSON(serializedObj)) {
            totalItems++;
            totalSize += serializedObj.length;
            
            const storageObj = JSON.parse(serializedObj);
            
            // Check if expired
            if (storageObj.expiry && storageObj.expiry < now) {
              expiredItems++;
            }
            
            // Track by project
            if (storageObj.projectId) {
              if (!projectItems[storageObj.projectId]) {
                projectItems[storageObj.projectId] = 0;
              }
              projectItems[storageObj.projectId]++;
            }
          }
        } catch (e) {
          console.warn(`Error analyzing item ${key}:`, e);
        }
      });
      
      return {
        totalItems,
        totalSize,
        expiredItems,
        projectItems,
        sizeInKB: Math.round(totalSize / 1024),
        sizeInMB: Math.round(totalSize / (1024 * 1024))
      };
    } catch (e) {
      console.error('Error getting storage stats:', e);
      return {
        totalItems: 0,
        totalSize: 0,
        expiredItems: 0,
        projectItems: {},
        sizeInKB: 0,
        sizeInMB: 0
      };
    }
  }

  /**
   * Migrate existing data to project-based structure
   * @param {string} sessionId - The session ID
   * @param {string} targetProjectId - Optional target project ID (uses default if not provided)
   * @returns {number} - Number of items migrated
   */
  migrateToProjectStructure(sessionId, targetProjectId = null) {
    try {
      const proj_id = this._getProjectId(targetProjectId);
      let migrated = 0;
      
      // Find all keys for this session that don't have project structure
      const keys = Object.keys(localStorage);
      const sessionPrefix = `${sessionId}:`;
      const projectPattern = `:project:`;
      
      keys.forEach(key => {
        if (key.startsWith(sessionPrefix) && 
            !key.includes(projectPattern) && 
            this._isSessionStorageManagerKey(key)) {
          try {
            // Get the old data
            const oldData = localStorage.getItem(key);
            if (oldData && this._isValidJSON(oldData)) {
              // Create new project-based key
              const keyWithoutSession = key.substring(sessionPrefix.length);
              const newKey = `${sessionId}:project:${proj_id}:${keyWithoutSession}`;
              
              // Parse old data and add project info
              const storageObj = JSON.parse(oldData);
              storageObj.projectId = proj_id;
              
              // Store with new key
              localStorage.setItem(newKey, JSON.stringify(storageObj));
              
              // Remove old key
              localStorage.removeItem(key);
              
              migrated++;
            }
          } catch (e) {
            console.warn(`Error migrating key ${key}:`, e);
          }
        }
      });
      
      if (migrated > 0) {
        console.log(`Migrated ${migrated} items to project structure`);
      }
      return migrated;
    } catch (e) {
      console.error('Error migrating to project structure:', e);
      return 0;
    }
  }


  /**
   * Mark an agent as having an active session
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @param {string} agentKey - The agent key for the session
   */
  markActiveAgentSession(sessionId, agentId, agentKey) {
      try {
          const activeSessionsKey = `${sessionId}:active_agent_sessions`;
          const activeSessions = this.getItem('active_sessions', sessionId, {});
          
          activeSessions[agentId] = {
              agentKey: agentKey,
              lastActive: Date.now(),
              hasUnsavedData: true
          };
          
          this.setItem('active_sessions', activeSessions, sessionId);
          return true;
      } catch (error) {
          console.error('Error marking active agent session:', error);
          return false;
      }
  }

  /**
   * Get all active agent sessions
   * @param {string} sessionId - The user's session ID
   * @returns {Object} Map of agentId to session info
   */
  getActiveAgentSessions(sessionId) {
      try {
          return this.getItem('active_sessions', sessionId, {});
      } catch (error) {
          console.error('Error getting active agent sessions:', error);
          return {};
      }
  }

  /**
   * Check if an agent has an active session
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   * @returns {boolean} True if agent has active session
   */
  hasActiveSession(sessionId, agentId) {
      try {
          const activeSessions = this.getActiveAgentSessions(sessionId);
          return !!(activeSessions[agentId] && activeSessions[agentId].agentKey);
      } catch (error) {
          console.error('Error checking active session:', error);
          return false;
      }
  }

  /**
   * Clear an agent's active session
   * @param {string} sessionId - The user's session ID
   * @param {string} agentId - The agent ID
   */
  clearActiveAgentSession(sessionId, agentId) {
      try {
          const activeSessions = this.getActiveAgentSessions(sessionId);
          delete activeSessions[agentId];
          this.setItem('active_sessions', activeSessions, sessionId);
          return true;
      } catch (error) {
          console.error('Error clearing active agent session:', error);
          return false;
      }
  }



}





// Create singleton instance
const sessionStorageManager = new SessionStorageManager();

// Auto-cleanup expired items on initialization (with error handling)
try {
  sessionStorageManager.cleanupExpiredItems();
} catch (e) {
  console.warn('Error during initial cleanup:', e);
}

export default sessionStorageManager;